import { Clock, MoreHorizontal, Pause, Play, Settings } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Platform, type ViewStyle } from 'react-native';
import { AnimatedCard } from '../../../components/animations';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';

export type WorkflowStatus = 'active' | 'paused' | 'error';

export interface Workflow {
  id: string;
  name: string;
  description: string;
  icon: ReactNode;
  status: WorkflowStatus;
  lastRun: string | null;
  executionCount: number;
  disabled?: boolean;
  statsOnly?: boolean;
}

interface WorkflowCardProps {
  workflow: Workflow;
  index: number;
  onRun?: () => void;
  onEdit?: () => void;
  onMore?: () => void;
}

const statusConfig: Record<
  WorkflowStatus,
  { color: string; bg: string; labelKey: string; glow: string }
> = {
  active: {
    color: colors.status.success.main,
    bg: colors.status.success.bg,
    labelKey: 'workflow.card.status.active',
    glow: colors.glow.green,
  },
  paused: {
    color: colors.status.warning.main,
    bg: colors.status.warning.bg,
    labelKey: 'workflow.card.status.paused',
    glow: colors.glow.amber,
  },
  error: {
    color: colors.status.error.main,
    bg: colors.status.error.bg,
    labelKey: 'workflow.card.status.error',
    glow: colors.glow.error,
  },
};

export function WorkflowCard({
  workflow,
  index,
  onRun,
  onEdit,
  onMore,
}: Readonly<WorkflowCardProps>) {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const status = statusConfig[workflow.status];
  const isDisabled = workflow.disabled === true;

  const webHoverStyle = Platform.select({
    web: {
      transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
      cursor: 'pointer',
      ...(isHovered && {
        boxShadow: `0 0 20px ${status.glow}, 0 4px 12px rgba(0, 0, 0, 0.15)`,
        borderColor: status.color,
        transform: [{ translateY: -2 }],
      }),
    } as ViewStyle,
    default: {},
  });

  const hoverProps = Platform.select({
    web: {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
    },
    default: {},
  });

  return (
    <AnimatedCard
      delay={index * 100}
      enablePress={!isDisabled}
      onPress={isDisabled ? undefined : onEdit}
    >
      <Box
        backgroundColor={colors.background.card}
        borderRadius="lg"
        borderWidth={1}
        borderColor={isHovered && !isDisabled ? status.color : colors.border.muted}
        padding="lg"
        opacity={isDisabled ? 0.55 : 1}
        style={isDisabled ? undefined : webHoverStyle}
        {...(isDisabled ? {} : hoverProps)}
      >
        <VStack spacing="lg">
          <HStack justify="between" align="start">
            <HStack spacing="md" align="center" style={{ flex: 1 }}>
              <Box
                backgroundColor={colors.background.secondary}
                borderRadius="md"
                padding="sm"
                alignItems="center"
                justifyContent="center"
              >
                {workflow.icon}
              </Box>
              <VStack spacing="xxs" style={{ flex: 1 }}>
                <Text variant="body" color={colors.text.primary} style={{ fontWeight: '600' }}>
                  {workflow.name}
                </Text>
                {isDisabled ? (
                  <Box
                    backgroundColor={colors.background.secondary}
                    paddingX="sm"
                    paddingY="xxs"
                    borderRadius="full"
                    alignSelf="flex-start"
                  >
                    <Text
                      variant="caption"
                      color={colors.text.secondary}
                      style={{ fontWeight: '500' }}
                    >
                      {t('workflow.card.comingSoon')}
                    </Text>
                  </Box>
                ) : (
                  <Box
                    backgroundColor={status.bg}
                    paddingX="sm"
                    paddingY="xxs"
                    borderRadius="full"
                    alignSelf="flex-start"
                  >
                    <HStack spacing="xs" align="center">
                      <Box
                        backgroundColor={status.color}
                        borderRadius="full"
                        style={{ width: 6, height: 6 }}
                      />
                      <Text variant="caption" color={status.color} style={{ fontWeight: '500' }}>
                        {t(status.labelKey)}
                      </Text>
                    </HStack>
                  </Box>
                )}
              </VStack>
            </HStack>
            {!isDisabled && onMore && (
              <Button variant="ghost" size="sm" onPress={onMore}>
                <MoreHorizontal size={18} color={colors.text.secondary} />
              </Button>
            )}
          </HStack>

          <Text variant="bodySmall" color={colors.text.secondary} numberOfLines={2}>
            {workflow.description}
          </Text>

          <HStack spacing="lg">
            <HStack spacing="xs" align="center">
              <Clock size={14} color={colors.text.tertiary} />
              <Text variant="caption" color={colors.text.tertiary}>
                {workflow.lastRun || t('workflow.card.neverRun')}
              </Text>
            </HStack>
            <Text variant="caption" color={colors.text.tertiary}>
              {workflow.executionCount} {t('workflow.card.executions')}
            </Text>
          </HStack>

          {!isDisabled && (
            <HStack spacing="sm">
              {onRun && (
                <Button
                  variant={workflow.status === 'active' ? 'secondary' : 'primary'}
                  size="sm"
                  onPress={onRun}
                  style={{ flex: 1 }}
                >
                  <HStack spacing="xs" align="center">
                    {workflow.status === 'active' ? (
                      <Pause size={14} color={colors.text.primary} />
                    ) : (
                      <Play size={14} color={colors.primary[900]} />
                    )}
                    <Text
                      variant="caption"
                      color={
                        workflow.status === 'active' ? colors.text.primary : colors.primary[900]
                      }
                      style={{ fontWeight: '600' }}
                    >
                      {workflow.status === 'active'
                        ? t('workflow.card.pauseButton')
                        : t('workflow.card.runButton')}
                    </Text>
                  </HStack>
                </Button>
              )}
              {onEdit && (
                <Button variant="secondary" size="sm" onPress={onEdit}>
                  <Settings size={14} color={colors.text.primary} />
                </Button>
              )}
            </HStack>
          )}
        </VStack>
      </Box>
    </AnimatedCard>
  );
}
